import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a South African invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A South African tax invoice must include: the words "Tax Invoice"; your business name, address, and VAT registration number; the customer name and address (and VAT number for invoices above ZAR 5,000); a unique invoice number; the invoice date; a description of goods or services; the VAT exclusive amount, VAT rate and amount, and the VAT inclusive total.',
      },
    },
    {
      '@type': 'Question',
      name: 'When must a business register for VAT in South Africa?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In South Africa, VAT registration with SARS is compulsory once your annual taxable turnover exceeds ZAR 1,000,000. Voluntary registration is available if turnover exceeds ZAR 50,000. The standard VAT rate is 15%.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT rate in South Africa?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The standard VAT rate in South Africa is 15%, which applies to most goods and services. Some supplies are zero-rated (0%), such as basic foodstuffs and exports. Others are VAT-exempt, such as certain financial services.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do South African freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'South African freelancers billing overseas clients can invoice in USD, GBP, EUR, or any agreed currency. Export services provided to non-residents are generally zero-rated for VAT. Use the international invoice type on Invilic to bill in any currency with your South African bank details.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a free invoice for South African businesses?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is a free invoice generator for South African businesses and freelancers. Create VAT tax invoices or standard invoices in ZAR or any other currency. Download a professional PDF instantly — free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'VAT Tax Invoice',
    subtitle: 'For VAT-registered SA businesses',
    features: ['15% VAT auto-calculated', 'VAT registration number field', 'Net + VAT + total breakdown', 'SARS-compliant format', 'ZAR currency'],
    to: '/create/vat',
    color: 'green',
    btnClass: 'bg-green-600 hover:bg-green-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below ZAR 1,000,000',
    features: ['No VAT required', 'ZAR currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, GBP, EUR billing', 'Zero-rated export invoices', 'SWIFT / bank transfer details', 'Multi-currency support'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorSouthAfricaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator South Africa: Free VAT Invoice Maker | Invilic"
        description="Free invoice generator for South African businesses and freelancers. Create 15% VAT tax invoices or standard invoices in ZAR. PDF download free, no sign-up required."
        canonical="https://www.invilic.com/invoice-generator-south-africa"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇿🇦 South Africa
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator South Africa
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for South African businesses, freelancers, and sole traders. Create SARS-compliant VAT tax invoices with 15% VAT auto-calculated, or standard invoices in ZAR. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200">
              Create VAT Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">VAT Tax Invoice vs Standard Invoice in South Africa</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-green-700 dark:text-green-400 mb-3">Use a VAT Tax Invoice if:</div>
              {[
                'Your annual taxable turnover exceeds ZAR 1,000,000',
                'You are registered for VAT with SARS',
                'Your client needs a VAT invoice to claim input tax credits',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Standard Invoice if:</div>
              {[
                'Your turnover is below the ZAR 1,000,000 VAT threshold',
                'You are a freelancer or sole trader not VAT-registered',
                'Your client does not need a VAT tax invoice',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">South African Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your South African Invoice for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF for SA businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-green-600/20 transition-all duration-200 text-lg">
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
